/**
 * @file
 * The Tabs class.
 */

import TabToggle from "./TabToggle.js";
import { isValidType, isValidClassList } from "../validate.js";
import { addClass, removeClass } from "../domHelpers.js";
import { keyPress, preventEvent } from "../eventHandlers.js";
import Component from "../Component.js";

class Tabs extends Component {
  /**
   * The DOM elements within the tabs.
   *
   * @protected
   *
   * @type {Object<HTMLElement, HTMLElement[]>}
   *
   * @property {HTMLElement}   tabs       - The tabs element.
   * @property {HTMLElement}   tabList    - The tab list element.
   * @property {HTMLElement[]} tabToggle  - An array of tab item toggles.
   * @property {HTMLElement[]} tabContent - An array of tab item contents.
   */
  _dom = {
    tabs: null,
    tabList: null,
    tabToggle: [],
    tabContent: [],
  };

  /**
   * The DOM elements within the tabs that cannot be reset or generated by the tabs itself.
   *
   * @protected
   *
   * @type {string[]}
   */
  _protectedDOMElements = ["tab"];

  /**
   * The query selectors used by the tabs.
   *
   * @protected
   *
   * @type {Object<string>}
   *
   * @property {string} tabList    - The query selector for the tab list.
   * @property {string} tabToggle  - The query selector for tab toggles.
   * @property {string} tabContent - The query selector for tab contents.
   */
  _selectors = {
    tabList: "",
    tabToggle: "",
    tabContent: "",
  };

  /**
   * The instantiated elements within the tabs.
   *
   * @protected
   *
   * @type {Object<TabToggle[]>}
   *
   * @property {TabToggle[]} tabToggle - The list of tab items.
   */
  _elements = {
    tabToggle: [],
  };

  /**
   * The CSS classes to apply when the tabs is in various states.
   *
   * @protected
   *
   * @type {Object<string, string[]>}
   *
   * @property {string|string[]} open         - The class(es) to apply when the tab is open.
   * @property {string|string[]} close        - The class(es) to apply when the tab is closed.
   * @property {string|string[]} transition   - The class(es) to apply when the tab is transitioning between states.
   * @property {string|string[]} initialize - The class(es) to apply when the tab is initializing.
   */
  _classes = {
    open: "show",
    close: "hide",
    transition: "transitioning",
    initialize: "initializing",
  };

  /**
   * The duration times (in milliseconds) for variouse aspects throughout the tabs.
   *
   * @protected
   *
   * @type {Object<number>}
   *
   * @property {number} transition - The duration time (in milliseconds) for the transition between open and closed states.
   * @property {number} open       - The duration time (in milliseconds) for the transition from closed to open states.
   * @property {number} close      - The duration time (in milliseconds) for the transition from open to closed states.
   */
  _durations = {
    transition: 250,
    open: -1,
    close: -1,
  };

  /**
   * A flag to set if focusing a tab item toggle will automatically activate it.
   *
   * @protected
   *
   * @type {boolean}
   */
  _automatic = false;

  /**
   * The index of the current child node.
   *
   * @protected
   *
   * @type {number}
   */
  _currentChild = 0;

  /**
   * The key used for storage.
   *
   * @protected
   *
   * @type {string}
   */
  _storageKey = "tabs";

  /**
   * Constructs a new `Tab`.
   *
   * @param {object}             options                                     - The options for generating the tab.
   * @param {HTMLElement}        [options.tabsElement]                       - The tab element in the DOM.
   * @param {string}             [options.tabListSelector = .tab-list]       - The query selector string for the tab list.
   * @param {string}             [options.tabToggleSelector = .tab-toggle]   - The query selector string for tab toggle.
   * @param {string}             [options.tabContentSelector = .tab-content] - The query selector string for tab content.
   * @param {?(string|string[])} [options.openClass = show]                  - The class to apply when a tab is "open".
   * @param {?(string|string[])} [options.closeClass = hide]                 - The class to apply when a tab is "closed".
   * @param {?(string|string[])} [options.transitionClass = null]            - The class to apply when a tab is transitioning between "open" and "closed" states.
   * @param {number}             [options.transitionDuration = 300]          - The duration of the transition between "open" and "closed" states (in milliseconds).
   * @param {number}             [options.openDuration = -1]                 - The duration of the transition from "closed" to "open" states (in milliseconds).
   * @param {number}             [options.closeDuration = -1]                - The duration of the transition from "open" to "closed" states (in milliseconds).
   * @param {boolean}            [options.automaticActivation = false]       - A flag to set if focusing a tab item toggle will automatically activate it.
   * @param {?string}            [options.prefix = graupl-]                  - The prefix to use for CSS custom properties.
   * @param {?string}            [options.key = null]                        - The key used to generate IDs throughout the tab.
   * @param {?(string|string[])} [options.initializeClass = initializing]    - The class to apply when a tabs are initialzing.
   * @param {boolean}            [options.initialize = false]                - A flag to initialize the tab immediately upon creation.
   */
  constructor({
    tabsElement,
    tabListSelector = ".tab-list",
    tabToggleSelector = ".tab-toggle",
    tabContentSelector = ".tab-content",
    openClass = "show",
    closeClass = "hide",
    transitionClass = null,
    transitionDuration = 300,
    openDuration = -1,
    closeDuration = -1,
    automaticActivation = false,
    prefix = "graupl-",
    key = null,
    initializeClass = "initializing",
    initialize = false,
  }) {
    super({
      prefix,
      key,
      initializeClass,
    });

    // Set DOM elements.
    this._dom.tabs = tabsElement;

    // Set DOM selectors.
    this._selectors.tabList = tabListSelector;
    this._selectors.tabToggle = tabToggleSelector;
    this._selectors.tabContent = tabContentSelector;

    // Set open/close classes.
    this._classes.open = openClass || "";
    this._classes.close = closeClass || "";
    this._classes.transition = transitionClass || "";

    // Set transition duration.
    this._durations.transition = transitionDuration;
    this._durations.open = openDuration;
    this._durations.close = closeDuration;

    // Set automatic activation.
    this._automatic = automaticActivation;

    if (initialize) {
      this.initialize();
    }
  }

  /**
   * Initializes the tabs.
   */
  initialize() {
    try {
      // Add the initialization class.
      addClass(this.initializeClass, this.dom.tabs);

      super.initialize();

      // Set up the DOM.
      this._setDOMElements();
      this._setIds();
      this._setAriaAttributes();
      this._setCustomProps();

      // Create the child elements.
      this._createChildElements();

      // Handle events.
      this._handleFocus();
      this._handleClick();
      this._handleKeydown();
      this._handleKeyup();

      // Store the component.
      this._store();

      // Make sure the first tab is open if no other tab is active.
      if (
        this.elements.tabToggle.filter((toggle) => toggle.isActive).length === 0
      ) {
        this.elements.tabToggle[0].show();
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Remove the initialization class.
      requestAnimationFrame(() => {
        removeClass(this.initializeClass, this.dom.tabs);
      });
    }
  }

  /**
   * The class(es) to apply when the tab is open.
   *
   * @type {string|string[]}
   *
   * @see _classes.open
   */
  get openClass() {
    return this._classes.open;
  }

  set openClass(value) {
    isValidClassList({ openClass: value });

    if (this._classes.open !== value) {
      this._classes.open = value;
    }
  }

  /**
   * The class(es) to apply when the tab is closed.
   *
   * @type {string|string[]}
   *
   * @see _classes.close
   */
  get closeClass() {
    return this._classes.close;
  }

  set closeClass(value) {
    isValidClassList({ closeClass: value });

    if (this._classes.close !== value) {
      this._classes.close = value;
    }
  }

  /**
   * The class(es) to apply when the tab is transitioning between open and closed.
   *
   * @type {string|string[]}
   *
   * @see _classes.transition
   */
  get transitionClass() {
    return this._classes.transition;
  }

  set transitionClass(value) {
    isValidClassList({ transitionClass: value });

    if (this._classes.transition !== value) {
      this._classes.transition = value;
    }
  }

  /**
   * The duration time (in milliseconds) for the transition between open and closed states.
   *
   * @type {number}
   *
   * @see _durations.transition
   */
  get transitionDuration() {
    return this._durations.transition;
  }

  set transitionDuration(value) {
    isValidType("number", { transitionDuration: value });

    if (this._durations.transition !== value) {
      this._durations.transition = value;
      this._setTransitionDurations();
    }
  }

  /**
   * The duration time (in milliseconds) for the transition from closed to open states.
   *
   * If openDuration is set to -1, the transitionDuration will be used instead.
   *
   * @type {number}
   *
   * @see _durations.open
   */
  get openDuration() {
    return this._durations.open === -1
      ? this.transitionDuration
      : this._durations.open;
  }

  set openDuration(value) {
    isValidType("number", { openDuration: value });

    if (this._durations.open !== value) {
      this._durations.open = value;
      this._setTransitionDurations();
    }
  }

  /**
   * The duration time (in milliseconds) for the transition from open to closed states.
   *
   * If closeDuration is set to -1, the transitionDuration will be used instead.
   *
   * @type {number}
   *
   * @see _durations.close
   */
  get closeDuration() {
    return this._durations.close === -1
      ? this.transitionDuration
      : this._durations.close;
  }

  set closeDuration(value) {
    isValidType("number", { closeDuration: value });

    if (this._durations.close !== value) {
      this._durations.close = value;
      this._setTransitionDurations();
    }
  }

  /**
   * A flag to set if focusing a tab item toggle will automatically activate it.
   *
   * @type {boolean}
   *
   * @see _automatic
   */
  get isAutomatic() {
    return this._automatic;
  }

  set isAutomatic(value) {
    isValidType("boolean", { isAutomatic: value });

    if (this._automatic !== value) {
      this._automatic = value;
    }
  }

  /**
   * The current index of the tab item.
   *
   * @readonly
   *
   * @type {number}
   *
   * @see _currentChild
   */
  get currentChild() {
    return this._currentChild;
  }

  set currentChild(value) {
    isValidType("number", { currentChild: value });

    if (
      this._currentChild !== value &&
      value >= 0 &&
      value < this.elements.tabToggle.length
    ) {
      this._currentChild = value;
    }
  }
  /**
   * The currently selected tab item.
   *
   * @readonly
   *
   * @type {TabToggle}
   */
  get currentTabToggle() {
    return this.elements.tabToggle[this.currentChild];
  }

  /**
   * Validates all aspects of the tabs to ensure proper functionality.
   *
   * Keys are altered to match the arguments passed in during creation where possible.
   *
   * @protected
   *
   * @return {boolean} - The result of the validation.
   */
  _validate() {
    super._validate();

    // Boolean checks.
    const booleans = {
      automaticActivation: this._automatic,
    };

    // Check the booleans.
    const booleanChecks = isValidType("boolean", booleans);

    // Handle boolean check failure.
    if (!booleanChecks) {
      this._errors.push(booleanChecks.message);
      this._valid = false;
    }

    return this._valid;
  }

  /**
   * Sets the IDs throughout the tabs.
   *
   * The generated IDs use the key and follow the format:
   *  - tabs: `tabs-${key}`
   *  - tab-list: `tab-list-${key}`
   *
   * @protected
   */
  _setIds() {
    this.dom.tabs.id = this.dom.tabs.id || `tabs-${this.key}`;
    this.dom.tabList.id = this.dom.tabList.id || `tab-list-${this.key}`;

    this._id = this.dom.tabs.id;
  }

  /**
   * Sets ARIA attributes throughout the tabs.
   *
   * @protected
   */
  _setAriaAttributes() {
    this.dom.tabList.setAttribute("role", "tablist");
  }

  /**
   * Sets custom props throughout the tabs.
   *
   * The custom properties are:
   *   - `--graupl-tabs-transition-duration`,
   *   - `--graupl-tabs-open-transition-duration`, and
   *   - `--graupl-tabs-close-transition-duration`.
   *
   * The prefix of `graupl-` can be changed by setting the tab's prefix value.
   *
   * @protected
   */
  _setCustomProps() {
    this.dom.tabs.style.setProperty(
      `--${this.prefix}tabs-transition-duration`,
      `${this.transitionDuration}ms`
    );

    this.dom.tabs.style.setProperty(
      `--${this.prefix}tabs-open-transition-duration`,
      `${this.openDuration}ms`
    );

    this.dom.tabs.style.setProperty(
      `--${this.prefix}tabs-close-transition-duration`,
      `${this.closeDuration}ms`
    );
  }

  /**
   * Sets all DOM elements within the tabs.
   *
   * Utilizes _setDOMElementType and
   * _resetDOMElementType.
   *
   * @protected
   */
  _setDOMElements() {
    this._resetDOMElementType("tabList");
    this._setDOMElementType("tabList", { context: this.dom.tabs });

    if (this.dom.tabList) {
      this._resetDOMElementType("tabToggle");
      this._setDOMElementType("tabToggle", { context: this.dom.tabList });
    }

    this._resetDOMElementType("tabContent");
    this._setDOMElementType("tabContent", { context: this.dom.tabs });
  }

  /**
   * Creates and initializes all tab toggles.
   *
   * @protected
   */
  _createChildElements() {
    this.dom.tabToggle.forEach((tabToggle, index) => {
      const toggle = new TabToggle({
        toggleElement: tabToggle,
        contentElement: this.dom.tabContent[index],
        parentTab: this,
      });

      toggle.initialize();

      this._elements.tabToggle.push(toggle);
    });
  }

  /**
   * Handles focus events throughout the tab for proper use.
   *
   * - Adds a `focus` listener to every tab item so when it gains focus,
   *   it will set the tab's current child to the index of the item.
   *
   * @protected
   */
  _handleFocus() {
    this.elements.tabToggle.forEach((toggle, index) => {
      this._addEventListener("focus", toggle.dom.toggle, () => {
        this.currentChild = index;

        if (this.isAutomatic) {
          this.currentTabToggle.show();
        }
      });
    });
  }

  /**
   * Handles click events throughout the tab item for proper use.
   *
   * - Adds a `pointerup` listener to the tab item toggles that will toggle each tab item.
   *
   * @protected
   */
  _handleClick() {
    this.elements.tabToggle.forEach((toggle, index) => {
      this._addEventListener("pointerup", toggle.dom.toggle, () => {
        this.currentChild = index;
        toggle.show();
      });
    });
  }

  /**
   * Handles keydown events throughout the tab item for proper use.
   *
   * This method exists to assist the _handleKeyup method.
   *
   * - Adds a `keydown` listener to all tab item toggles.
   *   - Blocks propagation on "Space", "Enter", "ArrowRight", "ArrowLeft", "Home", and "End" keys.
   */
  _handleKeydown() {
    this.dom.tabToggle.forEach((tabToggle) => {
      this._addEventListener("keydown", tabToggle, (event) => {
        const key = keyPress(event);
        const ToggleKeys = [
          "Space",
          "Enter",
          "ArrowRight",
          "ArrowLeft",
          "Home",
          "End",
        ];

        // Prevent default behavior for space and enter keys.
        if (ToggleKeys.includes(key)) {
          preventEvent(event);
        }
      });
    });
  }

  /**
   * Handles keyup events throughout the tab item for proper use.
   *
   * Adds the follow keybindings (explanations are taken from the WAI ARIA Practices Guide Tab Pattern):
   *
   * - `Enter` or `Space`:
   *   - When focus is on the tab link for a collapsed panel, expands the associated panel. If the implementation allows only one panel to be expanded, and if another panel is expanded, collapses that panel.
   *   - When focus is on the tab link for an expanded panel, collapses the panel if the implementation supports collapsing. Some implementations require one panel to be expanded at all times and allow only one panel to be expanded; so, they do not support a collapse function.
   * - `Tab`: Moves focus to the next focusable element; all focusable elements in the tab are included in the page `Tab` sequence.
   * - `Shift + Tab`: Moves focus to the previous focusable element; all focusable elements in the tab are included in the page `Tab` sequence.
   * - `Down Arrow` (Optional): If focus is on an tab link, moves focus to the next tab link. If focus is on the last tab link, either does nothing or moves focus to the first tab link.
   * - `Up Arrow` (Optional): If focus is on an tab link, moves focus to the previous tab link. If focus is on the first tab link, either does nothing or moves focus to the last tab link.
   * - `Home` (Optional): When focus is on an tab link, moves focus to the first tab link.
   * - `End` (Optional): When focus is on an tab link, moves focus to the last tab link.
   *
   * Note: When the above explanations mention "tab link", they are referring to the tab item toggle.
   */
  _handleKeyup() {
    this.dom.tabToggle.forEach((tabToggle) => {
      this._addEventListener("keyup", tabToggle, (event) => {
        const key = keyPress(event);

        switch (key) {
          case "Space":
          case "Enter":
            preventEvent(event);
            this.currentTabToggle.show();

            break;
          case "Home":
            preventEvent(event);
            this.focusFirstChild();

            break;
          case "End":
            preventEvent(event);
            this.focusLastChild();

            break;
          case "ArrowRight":
            preventEvent(event);
            this.focusNextChild();

            break;
          case "ArrowLeft":
            preventEvent(event);
            this.focusPreviousChild();

            break;
        }
      });
    });
  }

  /**
   * Focus the tab's current child.
   */
  focusCurrentChild() {
    if (this.currentChild !== -1) {
      this.currentTabToggle.focus();
    }
  }

  /**
   * Focuses the tab's child at a given index.
   *
   * @param {number} index - The index of the child to focus.
   */
  focusChild(index) {
    this.blurCurrentChild();
    this.currentChild = index;
    this.focusCurrentChild();
  }

  /**
   * Focuses the tab's first child.
   */
  focusFirstChild() {
    this.focusChild(0);
  }

  /**
   * Focus the tab's last child.
   */
  focusLastChild() {
    this.focusChild(this.elements.tabToggle.length - 1);
  }

  /**
   * Focus the tab's next child.
   */
  focusNextChild() {
    if (this.currentChild < this.elements.tabToggle.length - 1) {
      this.focusChild(this.currentChild + 1);
    } else {
      this.focusFirstChild();
    }
  }

  /**
   * Focus the tab's previous child.
   */
  focusPreviousChild() {
    if (this.currentChild > 0) {
      this.focusChild(this.currentChild - 1);
    } else {
      this.focusLastChild();
    }
  }

  /**
   * Blurs the tab's current child.
   */
  blurCurrentChild() {
    if (this.currentChild !== -1) {
      this.currentTabToggle.blur();
    }
  }
}

export default Tabs;
